import { Subscription } from './Subscription';
import { CompleteCallBack, ErrorCallBack, ObserverCallBack, Subscribable } from './Subscribable';
import { Observer } from './Observer';
import { Subscriber } from './Subscriber';

export type OperatorFunction<T, R> = (source: Observable<T>) => Observable<R>;

export class Observable<T, E = any> implements Subscribable<T, E> {
  private _producerFunction: ((subscriber: Subscriber<T, E>) => Subscription | (() => void) | void) | undefined;

  constructor(producerFunction?: (subscriber: Subscriber<T, E>) => Subscription | (() => void) | void) {
    this._producerFunction = producerFunction;
  }

  subscribe(d: ObserverCallBack<T>): Subscription;
  subscribe(d: ObserverCallBack<T>, e?: ErrorCallBack, c?: CompleteCallBack): Subscription;
  subscribe(observer: Partial<Observer<T, E>>): Subscription;
  subscribe(observer: Partial<Observer<T, E>> | ObserverCallBack<T>, e?: ErrorCallBack, c?: CompleteCallBack): Subscription {
    /*
        const observer = observerOrNext;

    const partialObserver: Partial<Observer<T, E>> = typeof observer === 'function'
      ? {
          next: observer,
          error: error,
          complete: complete
        }
      : {
          next: observer.next,
          error: observer.error,
          complete: observer.complete
        };
     */
    const partialObserver: Partial<Observer<T, E>> = {
      next: typeof observer === 'object' ? observer.next : observer,
      error: typeof observer === 'object' ? observer.error : e,
      complete: typeof observer === 'object' ? observer.complete : c
    };

    // Create a new internal subscriber for this specific subscription
    const internalSubscriber = new Subscriber({
      next: (data: T) => {
        partialObserver.next?.(data);
      },
      error: (e: E) => {
        partialObserver.error?.(e);
      },
      complete: () => {
        partialObserver.complete?.();
      }
    });

    if (this._producerFunction) {
      const teardownLogic = this._producerFunction(internalSubscriber);
      if (teardownLogic) {
        internalSubscriber.add(teardownLogic);
      }
    }

    return internalSubscriber;
  }

  // complete 되야지만 마지막 처리된다!
  toPromise(): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
      let lastValue: T | undefined;
      let subscription: Subscription;

      subscription = this.subscribe({
        next: (value: T) => {
          lastValue = value;
        },
        error: (err: E) => {
          reject(err);
          subscription.unsubscribe();
        },
        complete: () => {
          resolve(lastValue);
          subscription.unsubscribe();
        }
      });
    });
  }

  pipe(): Observable<T, E>;
  pipe<A>(op1: OperatorFunction<T, A>): Observable<A, any>;
  pipe<A, B>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>): Observable<B, any>;
  pipe<A, B, C>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>): Observable<C, any>;
  pipe<A, B, C, D>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>): Observable<D, any>;
  pipe<A, B, C, D, E>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>): Observable<E, any>;
  pipe<A, B, C, D, E, F>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>): Observable<F, any>;
  pipe<A, B, C, D, E, F, G>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>): Observable<G, any>;
  pipe<A, B, C, D, E, F, G, H>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>): Observable<H, any>;
  pipe<A, B, C, D, E, F, G, H, I>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>): Observable<I, any>;
  pipe<A, B, C, D, E, F, G, H, I, J>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>): Observable<J, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>): Observable<K, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>): Observable<L, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>): Observable<M, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>): Observable<N, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>): Observable<O, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>): Observable<P, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>): Observable<Q, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>): Observable<R, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>): Observable<S, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>, op20: OperatorFunction<S, U>): Observable<U, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>, op20: OperatorFunction<S, U>, op21: OperatorFunction<U, V>): Observable<V, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>, op20: OperatorFunction<S, U>, op21: OperatorFunction<U, V>, op22: OperatorFunction<V, W>): Observable<W, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>, op20: OperatorFunction<S, U>, op21: OperatorFunction<U, V>, op22: OperatorFunction<V, W>, op23: OperatorFunction<W, X>): Observable<X, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>, op20: OperatorFunction<S, U>, op21: OperatorFunction<U, V>, op22: OperatorFunction<V, W>, op23: OperatorFunction<W, X>, op24: OperatorFunction<X, Y>): Observable<Y, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>, op20: OperatorFunction<S, U>, op21: OperatorFunction<U, V>, op22: OperatorFunction<V, W>, op23: OperatorFunction<W, X>, op24: OperatorFunction<X, Y>, op25: OperatorFunction<Y, Z>): Observable<Z, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>, op20: OperatorFunction<S, U>, op21: OperatorFunction<U, V>, op22: OperatorFunction<V, W>, op23: OperatorFunction<W, X>, op24: OperatorFunction<X, Y>, op25: OperatorFunction<Y, Z>, op26: OperatorFunction<Z, AA>): Observable<AA, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>, op20: OperatorFunction<S, U>, op21: OperatorFunction<U, V>, op22: OperatorFunction<V, W>, op23: OperatorFunction<W, X>, op24: OperatorFunction<X, Y>, op25: OperatorFunction<Y, Z>, op26: OperatorFunction<Z, AA>, op27: OperatorFunction<AA, AB>): Observable<AB, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>, op20: OperatorFunction<S, U>, op21: OperatorFunction<U, V>, op22: OperatorFunction<V, W>, op23: OperatorFunction<W, X>, op24: OperatorFunction<X, Y>, op25: OperatorFunction<Y, Z>, op26: OperatorFunction<Z, AA>, op27: OperatorFunction<AA, AB>, op28: OperatorFunction<AB, AC>): Observable<AC, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>, op20: OperatorFunction<S, U>, op21: OperatorFunction<U, V>, op22: OperatorFunction<V, W>, op23: OperatorFunction<W, X>, op24: OperatorFunction<X, Y>, op25: OperatorFunction<Y, Z>, op26: OperatorFunction<Z, AA>, op27: OperatorFunction<AA, AB>, op28: OperatorFunction<AB, AC>, op29: OperatorFunction<AC, AD>): Observable<AD, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE>(op1: OperatorFunction<T, A>, op2: OperatorFunction<A, B>, op3: OperatorFunction<B, C>, op4: OperatorFunction<C, D>, op5: OperatorFunction<D, E>, op6: OperatorFunction<E, F>, op7: OperatorFunction<F, G>, op8: OperatorFunction<G, H>, op9: OperatorFunction<H, I>, op10: OperatorFunction<I, J>, op11: OperatorFunction<J, K>, op12: OperatorFunction<K, L>, op13: OperatorFunction<L, M>, op14: OperatorFunction<M, N>, op15: OperatorFunction<N, O>, op16: OperatorFunction<O, P>, op17: OperatorFunction<P, Q>, op18: OperatorFunction<Q, R>, op19: OperatorFunction<R, S>, op20: OperatorFunction<S, U>, op21: OperatorFunction<U, V>, op22: OperatorFunction<V, W>, op23: OperatorFunction<W, X>, op24: OperatorFunction<X, Y>, op25: OperatorFunction<Y, Z>, op26: OperatorFunction<Z, AA>, op27: OperatorFunction<AA, AB>, op28: OperatorFunction<AB, AC>, op29: OperatorFunction<AC, AD>, op30: OperatorFunction<AD, AE>): Observable<AE, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>
  ): Observable<AF, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>
  ): Observable<AG, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>
  ): Observable<AH, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>
  ): Observable<AI, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>
  ): Observable<AJ, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>
  ): Observable<AK, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>
  ): Observable<AL, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>
  ): Observable<AM, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>
  ): Observable<AN, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>,
    op40: OperatorFunction<AN, AO>
  ): Observable<AO, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO, AP>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>,
    op40: OperatorFunction<AN, AO>,
    op41: OperatorFunction<AO, AP>
  ): Observable<AP, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO, AP, AQ>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>,
    op40: OperatorFunction<AN, AO>,
    op41: OperatorFunction<AO, AP>,
    op42: OperatorFunction<AP, AQ>
  ): Observable<AQ, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO, AP, AQ, AR>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>,
    op40: OperatorFunction<AN, AO>,
    op41: OperatorFunction<AO, AP>,
    op42: OperatorFunction<AP, AQ>,
    op43: OperatorFunction<AQ, AR>
  ): Observable<AR, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO, AP, AQ, AR, AS>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>,
    op40: OperatorFunction<AN, AO>,
    op41: OperatorFunction<AO, AP>,
    op42: OperatorFunction<AP, AQ>,
    op43: OperatorFunction<AQ, AR>,
    op44: OperatorFunction<AR, AS>
  ): Observable<AS, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO, AP, AQ, AR, AS, AT>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>,
    op40: OperatorFunction<AN, AO>,
    op41: OperatorFunction<AO, AP>,
    op42: OperatorFunction<AP, AQ>,
    op43: OperatorFunction<AQ, AR>,
    op44: OperatorFunction<AR, AS>,
    op45: OperatorFunction<AS, AT>
  ): Observable<AT, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO, AP, AQ, AR, AS, AT, AU>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>,
    op40: OperatorFunction<AN, AO>,
    op41: OperatorFunction<AO, AP>,
    op42: OperatorFunction<AP, AQ>,
    op43: OperatorFunction<AQ, AR>,
    op44: OperatorFunction<AR, AS>,
    op45: OperatorFunction<AS, AT>,
    op46: OperatorFunction<AT, AU>
  ): Observable<AU, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO, AP, AQ, AR, AS, AT, AU, AV>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>,
    op40: OperatorFunction<AN, AO>,
    op41: OperatorFunction<AO, AP>,
    op42: OperatorFunction<AP, AQ>,
    op43: OperatorFunction<AQ, AR>,
    op44: OperatorFunction<AR, AS>,
    op45: OperatorFunction<AS, AT>,
    op46: OperatorFunction<AT, AU>,
    op47: OperatorFunction<AU, AV>
  ): Observable<AV, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO, AP, AQ, AR, AS, AT, AU, AV, AW>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>,
    op40: OperatorFunction<AN, AO>,
    op41: OperatorFunction<AO, AP>,
    op42: OperatorFunction<AP, AQ>,
    op43: OperatorFunction<AQ, AR>,
    op44: OperatorFunction<AR, AS>,
    op45: OperatorFunction<AS, AT>,
    op46: OperatorFunction<AT, AU>,
    op47: OperatorFunction<AU, AV>,
    op48: OperatorFunction<AV, AW>
  ): Observable<AW, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO, AP, AQ, AR, AS, AT, AU, AV, AW, AX>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>,
    op40: OperatorFunction<AN, AO>,
    op41: OperatorFunction<AO, AP>,
    op42: OperatorFunction<AP, AQ>,
    op43: OperatorFunction<AQ, AR>,
    op44: OperatorFunction<AR, AS>,
    op45: OperatorFunction<AS, AT>,
    op46: OperatorFunction<AT, AU>,
    op47: OperatorFunction<AU, AV>,
    op48: OperatorFunction<AV, AW>,
    op49: OperatorFunction<AW, AX>
  ): Observable<AX, any>;
  pipe<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO, AP, AQ, AR, AS, AT, AU, AV, AW, AX, AY>(
    op1: OperatorFunction<T, A>,
    op2: OperatorFunction<A, B>,
    op3: OperatorFunction<B, C>,
    op4: OperatorFunction<C, D>,
    op5: OperatorFunction<D, E>,
    op6: OperatorFunction<E, F>,
    op7: OperatorFunction<F, G>,
    op8: OperatorFunction<G, H>,
    op9: OperatorFunction<H, I>,
    op10: OperatorFunction<I, J>,
    op11: OperatorFunction<J, K>,
    op12: OperatorFunction<K, L>,
    op13: OperatorFunction<L, M>,
    op14: OperatorFunction<M, N>,
    op15: OperatorFunction<N, O>,
    op16: OperatorFunction<O, P>,
    op17: OperatorFunction<P, Q>,
    op18: OperatorFunction<Q, R>,
    op19: OperatorFunction<R, S>,
    op20: OperatorFunction<S, U>,
    op21: OperatorFunction<U, V>,
    op22: OperatorFunction<V, W>,
    op23: OperatorFunction<W, X>,
    op24: OperatorFunction<X, Y>,
    op25: OperatorFunction<Y, Z>,
    op26: OperatorFunction<Z, AA>,
    op27: OperatorFunction<AA, AB>,
    op28: OperatorFunction<AB, AC>,
    op29: OperatorFunction<AC, AD>,
    op30: OperatorFunction<AD, AE>,
    op31: OperatorFunction<AE, AF>,
    op32: OperatorFunction<AF, AG>,
    op33: OperatorFunction<AG, AH>,
    op34: OperatorFunction<AH, AI>,
    op35: OperatorFunction<AI, AJ>,
    op36: OperatorFunction<AJ, AK>,
    op37: OperatorFunction<AK, AL>,
    op38: OperatorFunction<AL, AM>,
    op39: OperatorFunction<AM, AN>,
    op40: OperatorFunction<AN, AO>,
    op41: OperatorFunction<AO, AP>,
    op42: OperatorFunction<AP, AQ>,
    op43: OperatorFunction<AQ, AR>,
    op44: OperatorFunction<AR, AS>,
    op45: OperatorFunction<AS, AT>,
    op46: OperatorFunction<AT, AU>,
    op47: OperatorFunction<AU, AV>,
    op48: OperatorFunction<AV, AW>,
    op49: OperatorFunction<AW, AX>,
    op50: OperatorFunction<AX, AY>
  ): Observable<AY, any>;
  pipe(...operators: OperatorFunction<any, any>[]): Observable<any, any> {
    if (operators.length === 0) {
      return this as any;
    }
    return operators.reduce((prev, operator) => operator(prev), this as any);
  }
}
